/**
 * Workflows P5-4 (DEC-098): the conversation-control steps (collect a reply, lock the composer, snooze, set the
 * language) and the reply-button options. Mirrors backend `app/core/constants/workflow_p5.py`; the step lists
 * (facing, waiting, conversation) live in `workflows.ts` next to the others.
 */

/** What a `collect_reply` answer must look like (fixed checks on the server, never a pattern the author writes). */
export const COLLECT_REPLY_FORMATS = ["any", "email", "phone", "number", "digits"] as const;
export type CollectReplyFormat = (typeof COLLECT_REPLY_FORMATS)[number];
/** The shortest and longest answer (characters): a new step starts at 1 to 200; no answer is longer than 500. */
export const COLLECT_REPLY_LIMITS = { minDefault: 1, maxDefault: 200, lengthMax: 500 } as const;
/** Saving to the visitor's e-mail or phone fixes the format; a name is free text. */
export const REPLY_FORMAT_FOR_TARGET: Readonly<Record<string, CollectReplyFormat>> = { "visitor.email": "email", "visitor.phone": "phone" };
/** Where a collected reply may be saved: a visitor field, a lead field, a run variable or a custom attribute. */
export const REPLY_SAVE_TO_PATTERN = /^(visitor\.(name|email|phone)|lead\.[a-z0-9_]{1,40}|var\.[a-z0-9_]{1,40}|(visitor|conversation|lead)\.attr\.[a-z][a-z0-9_]{0,39})$/;
/** Where the label of a chosen reply button may be saved: a run variable or a custom attribute. */
export const BUTTON_SAVE_TO_PATTERN = /^(var\.[a-z0-9_]{1,40}|(visitor|conversation|lead)\.attr\.[a-z][a-z0-9_]{0,39})$/;

/** The composer states a facing run asks of the chat client (`done.workflow.composer`, `workflow_step.payload.composer`). */
export const COMPOSER_STATES = ["open", "locked", "reply"] as const;
export type ComposerState = (typeof COMPOSER_STATES)[number];

/** The id of the "Start over" button a reply-buttons step can add (it counts toward the 8 buttons). */
export const START_OVER_ID = "start_over";
/** What the Start over button says when nothing else was written (server copy; translatable per workflow like any button). */
export const START_OVER_LABEL = { en: "Start over", fil: "Magsimula ulit" } as const;

/** A snooze lasts for a while, until a date and time, or until the office opens; a reply or the conversation closing wakes it early. */
export const SNOOZE_MODES = ["for", "until", "until_office_hours"] as const;
export type SnoozeMode = (typeof SNOOZE_MODES)[number];
export const SNOOZE_INTERRUPTS = ["visitor_reply", "staff_reply", "closed"] as const;

/** `set_language`: the translation languages plus `auto` (go back to detecting it). */
export const SET_LANGUAGE_CHOICES = ["en", "fil", "auto"] as const;
export type SetLanguageChoice = (typeof SET_LANGUAGE_CHOICES)[number];
